import React, { useState, useEffect, useCallback } from 'react';
import api, { getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { ShieldAlert, RefreshCw, Clock } from 'lucide-react';

const LIFECYCLE = ['SUBMITTED', 'UNDER_REVIEW', 'FOLLOW_UP_REQUIRED', 'CLOSED'];
const SEV_COLOR = { MINOR: 'bg-green-600', MODERATE: 'bg-amber-500', MAJOR: 'bg-orange-600', CRITICAL: 'bg-red-600' };
const STATE_COLOR = { SUBMITTED: 'bg-slate-500', UNDER_REVIEW: 'bg-blue-600', FOLLOW_UP_REQUIRED: 'bg-amber-600', CLOSED: 'bg-emerald-600' };

export default function IncidentReviewPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [toState, setToState] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/mobile/admin/incidents/reportable');
      setRows(res.data?.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/mobile/incidents/${id}`);
      setDetail(res.data?.data);
      setToState('');
      setNotes('');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const transition = async () => {
    if (!toState) { toast.error('Select a state'); return; }
    setSaving(true);
    try {
      await api.post(`/mobile/admin/incidents/${detail.id}/transition`, { toState, notes });
      toast.success(`Moved to ${toState.replace(/_/g, ' ')}`);
      await openDetail(detail.id);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="incident-review-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldAlert className="h-6 w-6" /> Incident Review</h1>
          <p className="text-muted-foreground">Potentially reportable incidents flagged for management review. This app does not submit to the NDIS.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="ir-refresh"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="ir-empty">No flagged incidents to review.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Incident</TableHead><TableHead>Severity</TableHead>
                  <TableHead>Categories</TableHead><TableHead>Review</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} data-testid={`ir-row-${r.id}`}>
                    <TableCell className="font-medium">{r.incidentNo}</TableCell>
                    <TableCell><Badge className={`${SEV_COLOR[r.severity] || 'bg-slate-500'} text-white`}>{r.severity}</Badge></TableCell>
                    <TableCell className="text-sm">{(r.reportableCategories || []).join(', ') || '—'}</TableCell>
                    <TableCell><Badge variant="secondary">{r.reviewStatus || 'PENDING_REVIEW'}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => openDetail(r.id)} data-testid={`ir-open-${r.id}`}>Review</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ir-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.incidentNo}
                  <Badge className={`${SEV_COLOR[detail.severity] || 'bg-slate-500'} text-white`}>{detail.severity}</Badge>
                  <Badge className={`${STATE_COLOR[detail.lifecycleState] || 'bg-slate-500'} text-white`}>{(detail.lifecycleState || '').replace(/_/g, ' ')}</Badge>
                </DialogTitle>
                <DialogDescription>{detail.incidentType} · {detail.category || 'Uncategorised'}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2 text-sm">
                <p><span className="text-muted-foreground">Description: </span>{detail.description}</p>
                {detail.immediateActions && <p><span className="text-muted-foreground">Immediate action: </span>{detail.immediateActions}</p>}
                {detail.bodyAreas?.length > 0 && <p><span className="text-muted-foreground">Body areas: </span>{detail.bodyAreas.join(', ')}</p>}
                {detail.witnessDetails?.length > 0 && (
                  <p><span className="text-muted-foreground">Witnesses: </span>{detail.witnessDetails.map(w => w.name).filter(Boolean).join(', ')}</p>
                )}
                {detail.reportableCategories?.length > 0 && (
                  <p className="text-red-600"><span className="text-muted-foreground">Reportable flags: </span>{detail.reportableCategories.join(', ')}</p>
                )}

                {/* Timeline */}
                <div>
                  <p className="font-semibold flex items-center gap-1 mb-2"><Clock className="h-4 w-4" /> Status timeline</p>
                  <ol className="space-y-2 border-l-2 border-muted pl-4">
                    {(detail.statusTimeline || []).map((t, i) => (
                      <li key={i} className="relative" data-testid={`ir-timeline-${i}`}>
                        <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-primary" />
                        <p className="font-medium">{(t.state || '').replace(/_/g, ' ')}</p>
                        <p className="text-xs text-muted-foreground">{t.by_name || 'system'} · {String(t.at).slice(0, 16).replace('T', ' ')}</p>
                        {t.notes && <p className="text-xs">{t.notes}</p>}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Transition */}
                {detail.canEdit && (
                  <div className="border-t pt-3 space-y-2">
                    <p className="font-semibold">Update state</p>
                    <Select value={toState} onValueChange={setToState}>
                      <SelectTrigger data-testid="ir-state-select"><SelectValue placeholder="Select new state" /></SelectTrigger>
                      <SelectContent>
                        {LIFECYCLE.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {toState === 'CLOSED' && !detail.canCloseSerious && (
                      <p className="text-xs text-red-600">Closing a serious/reportable incident requires an administrator.</p>
                    )}
                    <Textarea placeholder="Review notes (optional)" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ir-notes" />
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                {detail.canEdit && (
                  <Button onClick={transition} disabled={saving || !toState} data-testid="ir-transition-btn">
                    {saving ? 'Saving…' : 'Update state'}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
